import React from 'react';
import { match } from 'ts-pattern';

import {
  FF_DASHBOARD_NEW_CNY_HKD_CURRENCIES,
  FF_DASHBOARD_NEW_EUR_SEPA,
} from '@/data/featureFlags';
import { useFeatureFlag } from '@/hooks/useFeatureFlags';
import { CountryType } from '@/types/country';
import { CurrencyCodeType } from '@/types/currency';
import { FiatAccountType } from '@/types/fiatAccountType';

export interface PaymentMethod {
  currency: CurrencyCodeType;
  accountType: FiatAccountType;
}

const makePaymentMethod = (
  currency: CurrencyCodeType,
  accountType: FiatAccountType,
): PaymentMethod => ({
  currency,
  accountType,
});

const EUROZONE_COUNTRIES = [
  CountryType.AT,
  CountryType.BE,
  CountryType.CY,
  CountryType.DE,
  CountryType.EE,
  CountryType.ES,
  CountryType.FI,
  CountryType.FR,
  CountryType.GR,
  CountryType.HR,
  CountryType.IE,
  CountryType.IT,
  CountryType.LT,
  CountryType.LU,
  CountryType.LV,
  CountryType.MT,
  CountryType.NL,
  CountryType.PT,
  CountryType.SI,
  CountryType.SK,
] as const;

export const paymentMethodsByCountry = (
  country: CountryType,
  currency: CurrencyCodeType = CurrencyCodeType.USD,
): ReadonlyArray<PaymentMethod> =>
  match<CountryType, ReadonlyArray<PaymentMethod>>(country)
    .with(CountryType.unset, (): ReadonlyArray<PaymentMethod> => [])
    .with(
      ...EUROZONE_COUNTRIES,
      (): ReadonlyArray<PaymentMethod> => [
        makePaymentMethod(CurrencyCodeType.EUR, FiatAccountType.sepa),
        makePaymentMethod(CurrencyCodeType.USD, FiatAccountType.swift),
        makePaymentMethod(CurrencyCodeType.USDC, FiatAccountType.crypto),
      ],
    )
    .with(
      CountryType.US,
      (): ReadonlyArray<PaymentMethod> => [
        makePaymentMethod(CurrencyCodeType.USD, FiatAccountType.ach),
        makePaymentMethod(CurrencyCodeType.USD, FiatAccountType.wire),
        makePaymentMethod(CurrencyCodeType.USD, FiatAccountType.swift),
        makePaymentMethod(CurrencyCodeType.USDC, FiatAccountType.crypto),
      ],
    )
    .with(
      CountryType.BR,
      (): ReadonlyArray<PaymentMethod> => [
        makePaymentMethod(CurrencyCodeType.BRL, FiatAccountType.pix),
        makePaymentMethod(CurrencyCodeType.USD, FiatAccountType.swift),
        makePaymentMethod(CurrencyCodeType.USDC, FiatAccountType.crypto),
      ],
    )
    .with(
      CountryType.MX,
      (): ReadonlyArray<PaymentMethod> => [
        makePaymentMethod(CurrencyCodeType.MXN, FiatAccountType.spei),
        makePaymentMethod(CurrencyCodeType.USD, FiatAccountType.swift),
        makePaymentMethod(CurrencyCodeType.USDC, FiatAccountType.crypto),
      ],
    )
    .with(
      CountryType.CO,
      (): ReadonlyArray<PaymentMethod> => [
        makePaymentMethod(CurrencyCodeType.COP, FiatAccountType.cop),
        makePaymentMethod(CurrencyCodeType.USD, FiatAccountType.swift),
        makePaymentMethod(CurrencyCodeType.USDC, FiatAccountType.crypto),
      ],
    )
    .with(
      CountryType.AR,
      (): ReadonlyArray<PaymentMethod> => [
        makePaymentMethod(CurrencyCodeType.ARS, FiatAccountType.cvu),
        makePaymentMethod(CurrencyCodeType.USD, FiatAccountType.swift),
        makePaymentMethod(CurrencyCodeType.USDC, FiatAccountType.crypto),
      ],
    )
    .with(
      CountryType.SG,
      (): ReadonlyArray<PaymentMethod> => [
        makePaymentMethod(CurrencyCodeType.USD, FiatAccountType.swift),
        makePaymentMethod(CurrencyCodeType.USDC, FiatAccountType.crypto),
      ],
    )
    .with(
      CountryType.CN,
      (): ReadonlyArray<PaymentMethod> => [
        makePaymentMethod(CurrencyCodeType.CNY, FiatAccountType.swift),
        makePaymentMethod(CurrencyCodeType.USD, FiatAccountType.swift),
        makePaymentMethod(CurrencyCodeType.USDC, FiatAccountType.crypto),
      ],
    )
    .with(
      CountryType.HK,
      (): ReadonlyArray<PaymentMethod> => [
        makePaymentMethod(CurrencyCodeType.HKD, FiatAccountType.swift),
        makePaymentMethod(CurrencyCodeType.USD, FiatAccountType.swift),
        makePaymentMethod(CurrencyCodeType.USDC, FiatAccountType.crypto),
      ],
    )
    .otherwise(
      (): ReadonlyArray<PaymentMethod> => [
        makePaymentMethod(currency, FiatAccountType.swift),
        makePaymentMethod(CurrencyCodeType.USDC, FiatAccountType.crypto),
      ],
    );

const CNY_HKD_GATED_CURRENCIES: ReadonlySet<CurrencyCodeType> = new Set([
  CurrencyCodeType.CNY,
  CurrencyCodeType.HKD,
]);

const EUR_SEPA_GATED_CURRENCIES: ReadonlySet<CurrencyCodeType> = new Set([CurrencyCodeType.EUR]);

/**
 * Get payment methods available for a specific country and currency.
 * @param country The country to get payment methods for.
 * @param currency The currency to use for the default payment method.
 * @returns An array of payment methods available for the specified country and currency.
 */
export const usePaymentMethodsByCountry = (
  country: CountryType,
  currency: CurrencyCodeType = CurrencyCodeType.USD,
): ReadonlyArray<PaymentMethod> => {
  const isCnyHkdEnabled = useFeatureFlag(FF_DASHBOARD_NEW_CNY_HKD_CURRENCIES);
  const isEurSepaEnabled = useFeatureFlag(FF_DASHBOARD_NEW_EUR_SEPA);
  return React.useMemo((): ReadonlyArray<PaymentMethod> => {
    const methods = paymentMethodsByCountry(country, currency);
    return methods.filter((method: PaymentMethod): boolean => {
      if (!isCnyHkdEnabled && CNY_HKD_GATED_CURRENCIES.has(method.currency)) {
        return false;
      }
      if (!isEurSepaEnabled && EUR_SEPA_GATED_CURRENCIES.has(method.currency)) {
        return false;
      }
      return true;
    });
  }, [country, currency, isCnyHkdEnabled, isEurSepaEnabled]);
};
